import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps {
  id: string;
  message: string;
  variant?: ToastVariant;
  duration?: number;
  onClose: (id: string) => void;
  action?: ToastAction;
}

/**
 * Toast notification component with animations and auto-dismiss
 *
 * Usage:
 * ```tsx
 * <Toast
 *   id="unique-id"
 *   message="Changes saved successfully"
 *   variant="success"
 *   onClose={handleClose}
 * />
 * ```
 */
export const Toast: React.FC<ToastProps> = ({
  id,
  message,
  variant = 'info',
  duration = 5000,
  onClose,
  action,
}) => {
  const [progress, setProgress] = useState(100);

  // Auto-dismiss timer
  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);

      if (remaining === 0) {
        clearInterval(interval);
        onClose(id);
      }
    }, 16); // ~60fps

    return () => clearInterval(interval);
  }, [id, duration, onClose]);

  const variantConfig = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-green-500/10 border-green-500/30',
      iconColor: 'text-green-400',
      progressColor: 'bg-green-500',
    },
    error: {
      icon: XCircle,
      bgColor: 'bg-red-500/10 border-red-500/30',
      iconColor: 'text-red-400',
      progressColor: 'bg-red-500',
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-amber-500/10 border-amber-500/30',
      iconColor: 'text-amber-400',
      progressColor: 'bg-amber-500',
    },
    info: {
      icon: Info,
      bgColor: 'bg-blue-500/10 border-blue-500/30',
      iconColor: 'text-blue-400',
      progressColor: 'bg-blue-500',
    },
  };

  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.95 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
      className={`relative w-full max-w-sm rounded-lg border ${config.bgColor} bg-slate-800/95 backdrop-blur-sm shadow-lg overflow-hidden`}
    >
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-slate-700/50">
        <div
          className={`h-full ${config.progressColor} transition-all duration-100 ease-linear`}
          style={{ width: `${progress}%` }}
          aria-hidden="true"
        />
      </div>

      {/* Content */}
      <div className="flex items-start gap-3 p-4 pt-5">
        {/* Icon */}
        <Icon className={`w-5 h-5 flex-shrink-0 ${config.iconColor}`} aria-hidden="true" />

        {/* Message and action */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white leading-relaxed">{message}</p>
          {action && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              className="mt-2 text-sm font-medium text-st-primary hover:text-[#12C4BE] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-st-primary focus-visible:ring-offset-2 focus-visible:ring-offset-slate-800 rounded"
            >
              {action.label}
            </button>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose(id);
          }}
          className="flex-shrink-0 text-slate-400 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-800 rounded"
          aria-label="Close notification"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </motion.div>
  );
};

/**
 * Container for rendering stacked toast notifications
 */
export interface ToastContainerProps {
  toasts: ToastProps[];
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts }) => {
  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col gap-3 pointer-events-none"
      aria-live="polite"
      aria-atomic="false"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast {...toast} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default Toast;
